// Calls to the support API (logistic service, /support/v1 through the gateway).
// The session cookie goes with every call; X-Requested-By is required for
// cookie sign-in (the server's CSRF guard).
import serverURL from '../config/fetch/serverURL';
import {
  ERROR_CODES,
  formatReference,
  serverIdOf,
} from '../utils/userError';
import {
  VISITOR_CONVERSATION_FULL,
  VISITOR_EMAIL_INVALID,
  VISITOR_NAME_MISSING,
} from './composerModel';

export const SOURCE_APP = 'web';

const BASE_HEADERS = {
  Accept: 'application/json',
  'X-Requested-By': 'Browser',
};

const FRIENDLY = {
  400: "We couldn't send that. Check the message and attachments and try again.",
  401: 'Your session has expired. Please sign in again to contact support.',
  403: 'Support messages are switched off for your account.',
  413: 'That is too large to send. Try fewer or smaller files.',
  429: 'You have sent a lot of messages. Please wait a few minutes and try again.',
  503: 'Lunch.Team support is busy right now. Please try again in a minute.',
};

// Support reference codes (src/utils/userError.js) for each status.
const CODES = {
  400: ERROR_CODES.SUPPORT_SEND_REJECTED,
  401: ERROR_CODES.AUTH_SIGNED_OUT,
  403: ERROR_CODES.SUPPORT_OFF,
  413: ERROR_CODES.SUPPORT_TOO_LARGE,
  429: ERROR_CODES.SUPPORT_RATE_LIMITED,
  503: ERROR_CODES.SUPPORT_BUSY,
};

const DICTATION_CODES = {
  422: ERROR_CODES.SUPPORT_VOICE_UNCLEAR,
  503: ERROR_CODES.SUPPORT_VOICE_UNAVAILABLE,
};

const SERVER_DOWN =
  "Lunch.Team support isn't responding. Please try again in a few minutes.";
const UNEXPECTED = "Support couldn't take that just now. Please try again in a minute.";

// Dictation is the only voice call; its errors say so.
const DICTATION_FRIENDLY = {
  422: "We couldn't understand that recording. Try again, or type your message.",
  503: 'Voice is not available right now. Please type your message.',
};

// What the server said, when it said more than the status: its errors carry message (= detail) and
// error (the reason phrase, e.g. "Bad Request").
export function serverMessage(body) {
  if (!body || typeof body !== 'object') return '';
  const said = [body.message, body.detail].filter(
    value => typeof value === 'string' && value.trim()
  )[0];
  if (!said) return '';
  const phrase = [body.error, body.title].filter(
    value => typeof value === 'string'
  );
  return phrase.indexOf(said) >= 0 ? '' : said.trim();
}

// How long a 429 asks to wait: retryAfterSeconds in the body, else the Retry-After header (seconds);
// null when neither says.
export function retryAfterSeconds(body, response) {
  const fromBody = body && Number(body.retryAfterSeconds);
  if (fromBody > 0) return Math.ceil(fromBody);
  const header =
    response && response.headers && typeof response.headers.get === 'function'
      ? Number(response.headers.get('Retry-After'))
      : NaN;
  return header > 0 ? Math.ceil(header) : null;
}

// "a minute", "3 minutes": the wait in a sentence.
export function waitInWords(seconds) {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}

const WAIT_A_FEW_MINUTES = /Please wait a few minutes( and try again)?\./;

function failure(status, body, overrides, response) {
  let message =
    (overrides && overrides[status]) ||
    FRIENDLY[status] ||
    (status >= 500 ? SERVER_DOWN : UNEXPECTED);
  const wait = status === 429 ? retryAfterSeconds(body, response) : null;
  if (wait) {
    message = message.replace(WAIT_A_FEW_MINUTES, `Please try again in ${waitInWords(wait)}.`);
  }
  const error = new Error(message);
  error.status = status;
  error.retryAfterSeconds = wait;
  error.body = body;
  error.serverMessage = serverMessage(body);
  error.code =
    (overrides === DICTATION_FRIENDLY && DICTATION_CODES[status]) ||
    CODES[status] ||
    (status >= 500
      ? ERROR_CODES.SERVER_UNAVAILABLE
      : `SUPPORT-UNEXPECTED-${status}`);
  error.at = new Date();
  error.serverId = serverIdOf({ body, response });
  error.reference = formatReference(error.code, error.at, error.serverId);
  return error;
}

function request(path, options = {}, overrides) {
  return fetch(serverURL.api(path), {
    mode: 'cors',
    credentials: 'include',
    ...options,
    headers: { ...BASE_HEADERS, ...(options.headers || {}) },
  }).then(
    response =>
      response.text().then(text => {
        let body = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch (e) {
          body = { message: text };
        }
        if (!response.ok) {
          throw failure(response.status, body, overrides, response);
        }
        return body;
      }),
    cause => {
      const offline =
        typeof navigator !== 'undefined' && navigator.onLine === false;
      const error = new Error(
        offline
          ? "You're offline. Check your connection and try again."
          : 'We could not reach Lunch.Team. Check your connection and try again.'
      );
      error.isNetworkError = true;
      error.cause = cause;
      error.code = offline
        ? ERROR_CODES.NETWORK_OFFLINE
        : ERROR_CODES.SERVER_UNAVAILABLE;
      error.at = new Date();
      error.reference = formatReference(error.code, error.at);
      throw error;
    }
  );
}

export function getAvailability() {
  return request(`/support/v1/availability?sourceApp=${SOURCE_APP}`);
}

/** Builds the multipart body for POST /support/v1/reports. */
export function buildReportForm({
  clientReportId,
  conversationId,
  text,
  files,
  audio,
  context,
  breadcrumbs,
}) {
  const form = new FormData();
  form.append('sourceApp', SOURCE_APP);
  form.append('clientReportId', clientReportId);
  if (conversationId) {
    form.append('conversationId', conversationId);
  }
  if (text) {
    form.append('text', text);
  }
  if (context) {
    form.append('context', JSON.stringify(context));
  }
  if (breadcrumbs && breadcrumbs.length) {
    form.append('breadcrumbs', JSON.stringify(breadcrumbs));
  }
  (files || []).forEach(file => form.append('files', file, file.name));
  if (audio) {
    form.append('audio', audio.blob, audio.fileName);
  }
  return form;
}

export function sendReport(report) {
  return request('/support/v1/reports', {
    method: 'POST',
    body: buildReportForm(report),
  });
}

export function dictate({ blob, fileName, screenId, restaurantId }) {
  const form = new FormData();
  form.append('sourceApp', SOURCE_APP);
  if (screenId) {
    form.append('screenId', screenId);
  }
  // On a menu page the server gives the speech model the dish names.
  if (restaurantId !== undefined && restaurantId !== null) {
    form.append('restaurantId', String(restaurantId));
  }
  form.append('audio', blob, fileName);
  return request(
    '/support/v1/dictation',
    { method: 'POST', body: form },
    DICTATION_FRIENDLY
  );
}

// The customer's view of a conversation: staff-only notes (audience INTERNAL, such as the assistant's
// handoff reason) never show here, even for an account that also has the support inbox, which the
// server answers with the staff view. Messages without an audience are from before it existed.
export function customerView(conversation) {
  if (!conversation || !Array.isArray(conversation.messages))
    return conversation;
  return {
    ...conversation,
    messages: conversation.messages.filter(
      message => !message.audience || message.audience === 'PUBLIC'
    ),
  };
}

// The signed-in customer's own conversations, newest activity first (resumeFromServer).
export function listMyConversations(limit = 5) {
  return request(`/support/v1/conversations?limit=${limit}`).then(list =>
    Array.isArray(list) ? list : []
  );
}

export function getConversation(conversationId) {
  return request(
    `/support/v1/conversations/${encodeURIComponent(conversationId)}`
  ).then(customerView);
}

// ---- signed-out visitors (landing page) --------------------------------------
// No session: a visitor's conversation is reached with its token, which the server returns once, when
// the conversation is created, and this browser keeps (conversationStore). Text only.

export const VISITOR_TOKEN_HEADER = 'X-Support-Visitor-Token';

const VISITOR_FRIENDLY = {
  400: "We couldn't send that. Check your email address and message and try again.",
  403: "Support chat isn't available right now.",
  429: "You're sending messages too quickly. Please wait a few minutes.",
};

function sentence(text) {
  const trimmed = text.trim();
  const capital = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

/**
 * The visitor's view of a failed call. reason says what the composer can do about it:
 * 'conversation-full' (the server's cap on one conversation; a new one can take the message), 'email'
 * or 'name' (a detail the server turned down). A 400 shows the server's own words when they say what
 * is wrong; a rejected or empty message (the honeypot) keeps the friendly text.
 */
function visitorFailure(error) {
  const said = error && error.serverMessage;
  if (error && error.status === 429 && /conversation is full/i.test(said)) {
    error.message = VISITOR_CONVERSATION_FULL;
    error.reason = 'conversation-full';
  } else if (error && error.status === 400 && said) {
    if (/valid email/i.test(said)) {
      error.message = VISITOR_EMAIL_INVALID;
      error.reason = 'email';
    } else if (/name is required/i.test(said)) {
      error.message = VISITOR_NAME_MISSING;
      error.reason = 'name';
    } else if (/^text must be/i.test(said)) {
      error.message = 'Your message must be 1 to 2,000 characters.';
    } else if (!/^message (rejected|required)$/i.test(said)) {
      error.message = sentence(said);
    }
  }
  throw error;
}

function visitorHeaders(token) {
  return token ? { [VISITOR_TOKEN_HEADER]: token } : {};
}

/**
 * POST /support/v1/visitor/messages. A new conversation (no conversationId) needs the name and email
 * (company is optional); the next message in one needs its token. website is the honeypot, sent as it
 * is (empty from a person).
 * Resolves with {conversationId, visitorToken (on creation only), assistantReplying}.
 */
export function postVisitorMessage({
  conversationId,
  token,
  email,
  name,
  company,
  text,
  clientMessageId,
  screen,
  website,
}) {
  const body = { text, clientMessageId, website: website || '' };
  if (conversationId) body.conversationId = conversationId;
  if (email) body.email = email;
  if (name) body.name = name;
  if (company) body.company = company;
  if (screen) body.screen = screen;
  return request(
    '/support/v1/visitor/messages',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...visitorHeaders(token),
      },
      body: JSON.stringify(body),
    },
    VISITOR_FRIENDLY
  ).catch(visitorFailure);
}

/** The visitor's conversation (public messages only); 404 when it is gone or the token doesn't match. */
export function getVisitorConversation(conversationId, token) {
  return request(
    `/support/v1/visitor/conversations/${encodeURIComponent(conversationId)}`,
    { headers: visitorHeaders(token) },
    VISITOR_FRIENDLY
  ).then(customerView, visitorFailure);
}

// Voice ordering: the server reads the words against the menu in view and answers ORDER, CLARIFY or
// SUPPORT. It only proposes; nothing is ordered here.
export function interpretOrder({ text, restaurantId, screenId }) {
  return request('/support/v1/order-intents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sourceApp: SOURCE_APP,
      screenId,
      restaurantId,
      text,
    }),
  });
}

// What the customer did with an order intent: ADDED, PLACED (orderId), DISMISSED or FAILED. Best effort.
export function reportOrderOutcome(intentId, { outcome, orderId, detail }) {
  if (!intentId) return Promise.resolve();
  return request(
    `/support/v1/order-intents/${encodeURIComponent(intentId)}/outcome`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome, orderId, detail }),
    }
  ).catch(() => undefined);
}



// WEBPACK FOOTER //
// ./src/support/supportApi.js